import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import {
  ChatDetailsView,
  AddMessageToChatRequest,
  OpenAiChatMessageView,
  OpenAiSourceLocation,
  useAddMessageToOpenAiChat,
  useGetChatSource,
} from "../api-generated";
import { useQueryClient, QueryKey } from "@tanstack/react-query";
import OpenAiChatForm from "./OpenAiChatForm";
import CenteredLoading from "./CenteredLoading";
import OpenAiChatSettingsModal from "./OpenAiChatSettingsModal";
import OpenAiChatMessage from "./OpenAiChatMessage";
import MultiSelectMenu from "./MultiSelectMenu";
import { useSourceUrlDetails } from "../utils";
import { useScroll } from "../hooks/scrolling";
import { getSourceQueryKey, updateChatCacheWithMessages } from "../domain_data";
import ErrorLoading from "./ErrorLoading";
import { useMultiSelect } from "../hooks";
import { MultiSelectableBlob } from "../hooks/useMultiSelect";

interface Props {
  source: ChatDetailsView;
  //TODO: don't think I should be doing this
  queryKey: QueryKey;
}

const OpenAiChatSource: React.FC<Props> = ({ source, queryKey }) => {
  //settings
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [shouldTranslateMessageToDe, setShouldTranslateMessageToDe] =
    useState<boolean>(true);

  const getEmptyMultiSelectState = useCallback(
    (): number[][][] =>
      source.messageViewList.map((message) =>
        Array(message.blobs.length).fill([])
      ),
    [JSON.stringify(source.messageViewList)]
  );

  const multiSelectableBlobs: MultiSelectableBlob[][] = useMemo(() => {
    return source.messageViewList.map((message) =>
      message.blobs.map((b) => b.tokens)
    );
  }, [JSON.stringify(source.messageViewList)]);

  const {
    multiSelectedWords,
    multiSelectTokenIndeces,
    setIsMultiSelectOn,
    closeMultiSelect,
    isMultiSelectOn,
    handleToggleMultiSelect,
    removeWordFromMultiSelect,
  } = useMultiSelect(multiSelectableBlobs, getEmptyMultiSelectState);

  const scrollRef = useRef<HTMLDivElement>(null);
  const { scrollToBottom, scrollSlowlyToBottom } = useScroll(scrollRef);

  useEffect(() => {
    scrollToBottom();
  }, []);

  const [messageDraft, setMessageDraft] = useState<string>();

  const addMessageMutation = useAddMessageToOpenAiChat();
  const qc = useQueryClient();

  const handleSubmit = (formData: AddMessageToChatRequest): void => {
    addMessageMutation.mutate(
      {
        sourceId: source.id,
        data: formData,
      },
      {
        onSuccess: (newMessages: OpenAiChatMessageView[]) => {
          updateChatCacheWithMessages(source.id, newMessages, qc);
          setMessageDraft("");
          scrollSlowlyToBottom();
        },
      }
    );
    scrollSlowlyToBottom(); // scroll to bottom while loading
  };

  const location: OpenAiSourceLocation = {
    type: "openai_chat",
    sourceId: source.id,
  };

  return (
    <>
      <div
        ref={scrollRef}
        style={{
          height: "100%", // Fill the allotted height
          overflowY: "auto", // Make it scrollable
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div id="chat-messages-container" style={{ flexGrow: 1 }}>
          {source.messageViewList.length === 0 && (
            <div className="flex justify-center p-8">
              No messages in chat yet!
            </div>
          )}

          {source.messageViewList.length > 0 && (
            <div className="w-full flex justify-center">
              <div className="max-w-[1000px] flex flex-col gap-6 py-4">
                {source.messageViewList.map((messageView, messageIndex) => (
                  <OpenAiChatMessage
                    key={messageIndex}
                    sourceId={source.id}
                    location={{ ...location, messageIndex }}
                    message={messageView}
                    wordsInMultipleSelect={
                      multiSelectedWords.length !==
                      source.messageViewList.length
                        ? undefined
                        : multiSelectTokenIndeces[messageIndex]
                    }
                    onToggleInMultiSelect={(tokenIndex, sentenceIndex) =>
                      handleToggleMultiSelect({
                        blobGroupIndex: messageIndex,
                        blobIndex: sentenceIndex,
                        tokenIndex,
                      })
                    }
                    queryKey={queryKey}
                  />
                ))}
              </div>
            </div>
          )}

          {addMessageMutation.isLoading && (
            <div className="py-20">
              <CenteredLoading justEllipsis />
            </div>
          )}
        </div>

        <div className="border-top-gray-800 border border-x-0 border-b-0">
          {isMultiSelectOn ? (
            <MultiSelectMenu
              wordsSelected={multiSelectedWords}
              onClickRemove={removeWordFromMultiSelect}
              onCloseMenu={closeMultiSelect}
              location={location}
            />
          ) : (
            <OpenAiChatForm
              onSubmit={handleSubmit}
              isDisabled={addMessageMutation.isLoading}
              isSubmitting={addMessageMutation.isLoading}
              messageDraftStarter={messageDraft}
              onOpenModal={() => setIsModalOpen(true)}
              shouldTranslateMessageToDe={shouldTranslateMessageToDe}
              turnOnMultiSelect={() => {
                setIsMultiSelectOn(true);
                setIsModalOpen(false);
              }}
            />
          )}
        </div>
      </div>

      <OpenAiChatSettingsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        shouldTranslateMessageToDe={shouldTranslateMessageToDe}
        setShouldTranslateMessageToDe={setShouldTranslateMessageToDe}
      />
    </>
  );
};

const Wrapped: React.FC = () => {
  const { sourceId } = useSourceUrlDetails();
  const queryKey = getSourceQueryKey(sourceId!);
  const {
    data: source,
    isLoading,
    isError,
    error,
  } = useGetChatSource(sourceId!, {
    query: {
      queryKey,
      keepPreviousData: false,
    },
  });

  if (isLoading) {
    return <CenteredLoading />;
  }

  if (isError) {
    return <ErrorLoading error={error} />;
  }

  return <OpenAiChatSource source={source} queryKey={queryKey} />;
};

export default Wrapped;
